import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, usePortalAuth, extractErr } from "@/portal/PortalAuth";
import { PortalHeader, ChangePasswordGate, btn, ghost, card, inp } from "@/portal/PortalShell";
import { GuestReservations } from "@/portal/GuestReservations";
import { BookingJourney } from "@/portal/BookingJourney";
import { IdVerification } from "@/portal/IdVerification";

const HOUSE_INFO = [
  { label: "Wi-Fi", value: "Network + password provided at check-in via smart-lock instructions." },
  { label: "Parking", value: "Free on-site parking — please follow the parking layout shared at check-in." },
  { label: "Quiet hours", value: "10:00 PM – 7:00 AM to respect fellow housemates." },
  { label: "Laundry", value: "Shared laundry — please clean the lint trap after each use." },
  { label: "Kitchen", value: "Fully equipped; please wash your dishes after each use." },
  { label: "House rules", value: "No smoking indoors, no parties, registered guests only." },
  { label: "Emergency contact", value: "hello@masayahospitality.com · +(510) 935 2380" },
];

const CATEGORIES = ["Maintenance", "Housekeeping", "Supplies", "Wi-Fi / Internet", "Payment Question", "Reservation / Stay Question", "Noise / House Concern", "Other"];
const STATUS_COLORS = { Received: "bg-[#E8ECE7] text-[#4b5a48]", Assigned: "bg-[#F5F5F4] text-[#44403C]", "In Progress": "bg-[#FFEDD5] text-[#9A3412]", "Waiting on Guest": "bg-[#FEF3C7] text-[#92400E]", Resolved: "bg-[#DCFCE7] text-[#166534]", Closed: "bg-[#E7E5E4] text-[#57534E]" };

export default function GuestDashboard() {
  return <ChangePasswordGate><Inner /></ChangePasswordGate>;
}

function Inner() {
  const { user } = usePortalAuth();
  const [stay, setStay] = useState(null);
  const [requests, setRequests] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [showExt, setShowExt] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/guest/me"); setStay(data.stay);
      const { data: r } = await api.get("/guest/requests"); setRequests(r);
      const { data: ex } = await api.get("/guest/extensions"); setExtensions(ex);
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="guest-dashboard" className="px-6 md:px-12 pt-28 md:pt-32 pb-24">
      <div className="max-w-5xl mx-auto">
        <PortalHeader subtitle="Guest Portal" title={`Welcome, ${user.name}`} />

        {/* Stay */}
        <div className={card} data-testid="guest-stay-card">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Your stay</p>
          {stay ? (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Field label="Property / Unit" value={stay.unit_slug} />
              <Field label="Check-in" value={stay.check_in} />
              <Field label="Expected check-out" value={stay.check_out} />
              <Field label="Monthly rate" value={`$${Number(stay.monthly_rate).toLocaleString()}`} />
              <Field label="Next payment amount" value={stay.next_payment_amount ? `$${Number(stay.next_payment_amount).toLocaleString()}` : "—"} />
              <Field label="Payment due" value={stay.next_payment_due || "—"} />
              <Field label="Payment status" value={<span className={`inline-block px-2 py-1 rounded-full text-xs ${STATUS_COLORS[stay.payment_status] || "bg-[#F5F5F4] text-[#44403C]"}`}>{stay.payment_status}</span>} />
            </div>
          ) : <p className="mt-3 text-[#78716C]">No active stay linked to your account yet. Please contact Masaya Hospitality.</p>}
        </div>

        <BookingJourney />
        <IdVerification />
        <GuestReservations />

        {/* House info */}
        <div className={`${card} mt-6`} data-testid="guest-house-info">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">House information</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {HOUSE_INFO.map((h) => <div key={h.label}><p className="text-sm font-medium text-[#1C1917]">{h.label}</p><p className="text-sm text-[#44403C] font-light mt-1">{h.value}</p></div>)}
          </div>
        </div>

        {/* Message / Request form */}
        <RequestForm onCreated={load} />

        {/* Requests list */}
        <div className={`${card} mt-6`} data-testid="guest-requests">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Your requests</p>
          {requests.length === 0 && <p className="mt-3 text-[#78716C]">No requests submitted yet.</p>}
          <div className="mt-4 space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="border border-[#E7E5E4] rounded-xl p-4" data-testid={`request-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="font-medium text-[#1C1917]">{r.category}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[r.status] || "bg-[#F5F5F4] text-[#44403C]"}`}>{r.status}</span>
                </div>
                <p className="mt-2 text-sm text-[#44403C]">{r.message}</p>
                {r.guest_updates?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-[#57534E]">
                    {r.guest_updates.slice(-3).map((u, i) => <li key={i}>• {u.text}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Extensions */}
        <div className={`${card} mt-6`} data-testid="guest-extensions">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Stay extensions</p>
            <button className={btn} onClick={() => setShowExt(true)} data-testid="request-extension-btn">Request to Extend My Stay</button>
          </div>
          {extensions.length === 0 && <p className="mt-3 text-[#78716C]">No extension requests yet.</p>}
          <div className="mt-4 space-y-2 text-sm">
            {extensions.map((x) => (
              <div key={x.id} className="flex items-center justify-between border border-[#E7E5E4] rounded-lg px-3 py-2">
                <span>{x.current_checkout} → {x.requested_checkout}</span>
                <span className="text-xs text-[#8A9A86] uppercase tracking-wider">{x.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showExt && <ExtensionModal onClose={() => setShowExt(false)} currentCheckout={stay?.check_out || ""} onCreated={load} />}
    </section>
  );
}

function Field({ label, value }) { return (<div><p className="text-xs uppercase tracking-wider text-[#78716C]">{label}</p><p className="mt-1 text-[#1C1917]">{value}</p></div>); }

function RequestForm({ onCreated }) {
  const [category, setCategory] = useState("Maintenance");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please describe your request."); return; }
    setBusy(true);
    try {
      await api.post("/guest/requests", { category, message });
      setMessage(""); toast.success("Request submitted."); onCreated();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className={`${card} mt-6`} data-testid="new-request-form">
      <p className="text-xs tracking-[0.2em] uppercase text-[#8A9A86] font-medium">Message Masaya Hospitality</p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
        <div>
          <label className="text-sm text-[#1C1917] mb-1 block">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="req-category" className={inp}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
        <div>
          <label className="text-sm text-[#1C1917] mb-1 block">Message</label>
          <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="req-message" placeholder="Describe your request or issue..." className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] p-3 text-sm resize-none" />
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="req-submit" className={`${btn} mt-4`}>{busy ? "Sending…" : "Submit Request"}</button>
    </form>
  );
}

function ExtensionModal({ onClose, currentCheckout, onCreated }) {
  const [current, setCurrent] = useState(currentCheckout);
  const [requested, setRequested] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/guest/extensions", { current_checkout: current, requested_checkout: requested, message });
      toast.success("Extension request received. Our Reservation Team will review availability and contact you.");
      onCreated(); onClose();
    } catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl border border-[#E7E5E4] p-8 w-full max-w-md" data-testid="extension-modal">
        <h3 className="text-2xl font-normal tracking-tight text-[#1C1917]">Request to Extend My Stay</h3>
        <p className="mt-2 text-sm text-[#78716C]">We'll review availability and get back to you.</p>
        <div className="mt-6 space-y-4">
          <div><label className="text-sm text-[#1C1917] mb-1 block">Current check-out</label><input type="date" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="ext-current" className={inp} /></div>
          <div><label className="text-sm text-[#1C1917] mb-1 block">Preferred new check-out</label><input type="date" required value={requested} onChange={(e) => setRequested(e.target.value)} data-testid="ext-requested" className={inp} /></div>
          <div><label className="text-sm text-[#1C1917] mb-1 block">Message (optional)</label><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="ext-message" className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] p-3 text-sm resize-none" /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className={ghost}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="ext-submit" className={btn}>{busy ? "Sending…" : "Send request"}</button>
        </div>
      </form>
    </div>
  );
}
